import type { ReactNode } from 'react'

export interface EmptyStateProps {
  readonly icon?: ReactNode
  readonly title: string
  readonly detail?: string
  readonly action?: ReactNode
  /**
   * UID-294 (§9) — an additive visual variant, NOT a restyle of the shared
   * default: `'panel'` (default) is the existing plain look every other
   * vertical already renders unmodified; `'dashed'` is the design's dashed-
   * border empty box (`Fictiv Platform.dc.html` ~L3442: `padding:56px 20px;
   * border:1px dashed var(--border-2);border-radius:4px`, bold 14.5px title
   * + a muted 13px subtitle, no icon). Only adds a second class
   * (`empty-state-panel-dashed`) alongside the untouched base
   * `empty-state-panel` — every existing consumer that never passes
   * `variant` keeps the exact same DOM/CSS as before. The dashed variant's
   * CSS lives in `rfq.css` (this ticket's file scope keeps `ui.css` to the
   * tab-strip margin only) — its only consumer today is the RFQ list tabs'
   * per-tab empty states; promote it to `ui.css` if a second vertical ever
   * adopts it.
   */
  readonly variant?: 'panel' | 'dashed'
}

/**
 * The one reusable empty/failed-load state (error kit — see the UID-27
 * ticket's "build it into this foundation" list, and components.md "Empty
 * state": icon + heading + body copy + optional CTA, "one consistent
 * pattern reused everywhere"). Every vertical (WS-2..5) renders this instead
 * of inventing its own "no results" markup — e.g. "No parts match your
 * search," "You're not a member of this workspace."
 *
 * Doubles as the failed-load state: pass a retry `action` (a Button) and an
 * error-flavored title/detail — this component doesn't prescribe copy, only
 * the layout, so both "empty" and "couldn't load" read consistently.
 */
export function EmptyState({ icon, title, detail, action, variant = 'panel' }: EmptyStateProps) {
  // `<output>` carries an implicit role="status" (HTML-AAM) — the semantic
  // element biome's a11y rule prefers over a bare `role="status"` div.
  const panelClassName =
    variant === 'dashed' ? 'empty-state-panel empty-state-panel-dashed' : 'empty-state-panel'
  return (
    <output className={panelClassName}>
      {icon && (
        <span className="empty-state-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <p className="empty-state-title">{title}</p>
      {detail && <p className="empty-state-detail">{detail}</p>}
      {action && <div className="empty-state-action">{action}</div>}
    </output>
  )
}
